import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CookieService } from '@core/services/cookie.service';
import { EnvironmentService } from '@core/services/environment.service';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, tap } from 'rxjs/operators';

const COOKIE_NAME = 'cookie-consent';
const COOKIE_SHOW_CONSENT_NAME = 'cookie-show-consent';

@Injectable({
    providedIn: 'root',
})
export class CookieConsentService {
    private _consentChanged = new BehaviorSubject<boolean | null>(null);
    showConsentModal = this._consentChanged.pipe(
        map(consent => consent === null)
    );

    consentChanged = this._consentChanged.pipe(
        distinctUntilChanged(),
        tap(consent => {
            if (this.environmentService.isBrowser()) {
                this.updateShowConsentCookie(consent);
            }
        }),
        map(consent => consent === true),
        shareReplay(1)
    );

    constructor(
        private cookieService: CookieService,
        private environmentService: EnvironmentService,
        @Inject(DOCUMENT) private document: Document
    ) {
        const initialConsent = this.readConsentFromCookie();
        this._consentChanged.next(initialConsent);
    }

    private readConsentFromCookie() {
        const consent = this.cookieService.getCookie(COOKIE_NAME);
        if (consent === undefined) {
            return null;
        }
        if (this.environmentService.isServer()) {
            // assume no consent given on backend by default
            return false;
        }

        // There was a mistake in the beginning and the SameSite attribute was set to Strict which causes problems
        // with people coming directly from google. This way it gets reset to lax
        this.cookieService.setCookie(COOKIE_NAME, consent, 31536000);
        return consent === 'true';
    }

    private updateShowConsentCookie(consent: boolean | null) {
        if (consent === null) {
            if (
                this.cookieService.getCookie(COOKIE_SHOW_CONSENT_NAME) !==
                undefined
            ) {
                this.cookieService.deleteCookie(COOKIE_SHOW_CONSENT_NAME);
            }
        } else {
            this.cookieService.setCookie(
                COOKIE_SHOW_CONSENT_NAME,
                'false',
                31536000
            );
        }
    }

    accept() {
        this.cookieService.setCookie(COOKIE_NAME, 'true', 31536000);
        this._consentChanged.next(true);
    }

    decline() {
        this.cookieService.setCookie(COOKIE_NAME, 'false', 31536000);
        this._consentChanged.next(false);
    }
}
